/**
 * Custom hook for save/export functionality in Data Adjustments
 * 
 * Responsibilities:
 * - Multi-file export loop with file index tracking
 * - CSV generation with German locale formatting
 * - Info file generation
 * - FileNameModal state management
 * - Auto-advance through files
 */

import { useState, useCallback, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { formatUTCTimestamp } from '../../../utils/dateFormatters';
import type { 
  InfoType, 
  DateTimeState,
  AdjustmentsUIAction 
} from '../types/dataAdjustments.types';

interface DataPoint {
  UTC: string;
  [key: string]: string | number | null | undefined;
}

interface UseAdjustmentsSaveProps {
  adjustedData: InfoType[];
  processedDataFrames: Array<unknown>;
  startTime: DateTimeState;
  endTime: DateTimeState;
  timeStepSize: string;
  offset: string;
  selectedMethods: { [key: string]: string };
  intrplMaxValues: { [key: string]: string };
  dispatch: Dispatch<AdjustmentsUIAction>;
}

interface UseAdjustmentsSaveReturn {
  // State
  saveDataName: string;
  isNameModalOpen: boolean;
  originalDataName: string;
  originalFileName: string;
  currentFileIndex: number;
  isLoading: boolean;
  
  // Error/Success
  error: string | null;
  successMessage: string | null;
  
  // Setters
  setSaveDataName: (name: string) => void;
  setIsNameModalOpen: (open: boolean) => void;
  setError: (error: string | null) => void;
  setSuccessMessage: (message: string | null) => void;
  
  // Actions
  saveData: () => Promise<void>;
  openSaveModal: () => void;
  downloadInfoFile: () => void;
  generateInfoFileContent: () => string;
  
  // Reset
  resetSave: () => void;
}

export const useAdjustmentsSave = ({
  adjustedData,
  processedDataFrames,
  startTime,
  endTime,
  timeStepSize,
  offset,
  selectedMethods,
  intrplMaxValues,
  dispatch,
}: UseAdjustmentsSaveProps): UseAdjustmentsSaveReturn => {
  const { t } = useTranslation('dataAdjustments');
  
  // Modal state
  const [saveDataName, setSaveDataName] = useState<string>('');
  const [isNameModalOpen, setIsNameModalOpen] = useState<boolean>(false);
  const [originalDataName, setOriginalDataName] = useState<string>('');
  const [originalFileName, setOriginalFileName] = useState<string>('');
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Message state
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /**
   * Generate info file content with processing parameters
   */
  const generateInfoFileContent = useCallback((): string => {
    const now = new Date();
    const timestamp = now.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    let content = `${t('infoFile.header')}\n`;
    content += `${'='.repeat(60)}\n\n`;

    // Generation timestamp
    content += `${t('infoFile.generatedAt')}: ${timestamp}\n\n`;

    // Uploaded files with original timestep and offset
    if (adjustedData && adjustedData.length > 0) {
      content += `${t('infoFile.uploadedFiles')}:\n`;
      content += `${'-'.repeat(40)}\n`;
      adjustedData.forEach((info) => {
        content += `  - ${info['Name der Datei']}\n`;
        content += `    ${t('infoFile.originalTimestep')}: ${info['Zeitschrittweite [min]']} min\n`;
        content += `    ${t('infoFile.originalOffset')}: ${info['Offset [min]']} min\n`;
      });
      content += `\n`;
    }

    // Helper to format ISO date to dd.mm.yyyy HH:mm
    const formatDateToGerman = (isoString: string): string => {
      try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return isoString;
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
      } catch {
        return isoString;
      }
    };

    // Processing parameters
    content += `${t('infoFile.parameters')}:\n`;
    content += `${'-'.repeat(40)}\n`;

    if (startTime.value) {
      content += `${t('infoFile.startTime')}: ${formatDateToGerman(startTime.value)}\n`;
    }
    if (endTime.value) {
      content += `${t('infoFile.endTime')}: ${formatDateToGerman(endTime.value)}\n`;
    }
    if (timeStepSize) {
      content += `${t('infoFile.timeStepSize')}: ${timeStepSize} min\n`;
    }
    if (offset) {
      content += `${t('infoFile.offset')}: ${offset} min\n`;
    }

    // Selected methods
    if (Object.keys(selectedMethods).length > 0) {
      content += `\n${t('infoFile.selectedMethods')}:\n`;
      Object.entries(selectedMethods).forEach(([filename, method]) => {
        content += `  - ${filename}: ${method}\n`;
      });
    }

    // Interpolation max values
    if (Object.keys(intrplMaxValues).length > 0) {
      content += `\n${t('infoFile.interpolationMax')}:\n`;
      Object.entries(intrplMaxValues).forEach(([filename, maxVal]) => {
        const displayValue = maxVal || t('infoFile.noValue');
        content += `  - ${filename}: ${displayValue} min\n`;
      });
    }

    content += `\n${'='.repeat(60)}\n`;

    return content;
  }, [t, adjustedData, startTime.value, endTime.value, timeStepSize, offset, selectedMethods, intrplMaxValues]);

  /**
   * Download info file, named to match the user-chosen filename (per-file).
   * Falls back to `info.txt` if no baseFileName is provided.
   * Matches W6/W7 convention: `${baseFileName}_info.txt`.
   */
  const downloadInfoFile = useCallback((baseFileName?: string) => {
    try {
      const infoContent = generateInfoFileContent();
      const blob = new Blob([infoContent], { type: 'text/plain;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = baseFileName ? `${baseFileName}_info.txt` : 'info.txt';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading info file:', err);
      // Don't show error to user as this is a supplementary file
    }
  }, [generateInfoFileContent]);

  /**
   * Open save modal for first file
   */
  const openSaveModal = useCallback(() => {
    if (!adjustedData.length || !processedDataFrames.length) {
      setError(t('errors.noDataToSave'));
      return;
    }

    const info = adjustedData[currentFileIndex];
    setOriginalFileName(info['Name der Datei']);
    setOriginalDataName(info['Name der Messreihe']);
    setIsNameModalOpen(true);
  }, [adjustedData, processedDataFrames.length, currentFileIndex, t]);

  /**
   * Save data to CSV file
   */
  const saveData = useCallback(async () => {
    try {
      // Check if we have data to save
      if (!adjustedData.length || !processedDataFrames.length) {
        setError(t('errors.noDataToSave'));
        return;
      }

      if (!saveDataName) {
        const info = adjustedData[currentFileIndex];
        setOriginalFileName(info['Name der Datei']);
        setOriginalDataName(info['Name der Messreihe']);
        setIsNameModalOpen(true);
        return;
      }

      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);

      const i = currentFileIndex;
      if (i < processedDataFrames.length) {
        const series = processedDataFrames[i];

        if (!series || !saveDataName) {
          return;
        }

        // Create CSV content (semicolon separator for German locale)
        const csvLines: string[] = [`UTC;${originalDataName}`];

        if (Array.isArray(series)) {
          (series as DataPoint[]).forEach((point: DataPoint) => {
            const utcTime = formatUTCTimestamp(point.UTC);
            const value = point[originalDataName];
            if (value !== null && value !== undefined) {
              const valueStr = isNaN(Number(value)) ? 'nan' : String(value);
              csvLines.push(`${utcTime};${valueStr}`);
            }
          });
        }

        const csvContent = csvLines.join('\n');

        // Create Blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${saveDataName}.csv`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        // Download a matching info file for EVERY processed CSV.
        // Matches W6/W7 convention: each output CSV gets a `${name}_info.txt`
        // sibling so the user can match data ↔ metadata after download.
        downloadInfoFile(saveDataName);

        setSuccessMessage(t('messages.downloadSuccess'));

        // Move to next file
        if (i + 1 < processedDataFrames.length) {
          const nextInfo = adjustedData[i + 1];
          setOriginalFileName(nextInfo['Name der Datei']);
          setOriginalDataName(nextInfo['Name der Messreihe']);
          setCurrentFileIndex(i + 1);
          setSaveDataName(''); // Reset name for next file
          setIsNameModalOpen(true);
        } else {
          // All files saved - reset
          setCurrentFileIndex(0);
          setSaveDataName('');
          setOriginalFileName('');
          setOriginalDataName('');
          setIsNameModalOpen(false);
          
          // Mark step 5 as completed
          dispatch({ type: 'MARK_STEP_COMPLETED', payload: 5 });
        }
      }

      setSuccessMessage(t('messages.dataSaved'));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('errors.unknownError', { defaultValue: 'An unknown error occurred' })
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    adjustedData,
    processedDataFrames,
    saveDataName,
    currentFileIndex,
    originalDataName,
    t,
    dispatch,
    downloadInfoFile,
  ]);

  /**
   * Reset all save state
   */
  const resetSave = useCallback(() => {
    setSaveDataName('');
    setIsNameModalOpen(false);
    setOriginalDataName('');
    setOriginalFileName('');
    setCurrentFileIndex(0);
    setIsLoading(false);
    setError(null);
    setSuccessMessage(null);
  }, []);

  return {
    // State
    saveDataName,
    isNameModalOpen,
    originalDataName,
    originalFileName,
    currentFileIndex,
    isLoading,
    
    // Error/Success
    error,
    successMessage,
    
    // Setters
    setSaveDataName,
    setIsNameModalOpen,
    setError,
    setSuccessMessage,
    
    // Actions
    saveData,
    openSaveModal,
    downloadInfoFile,
    generateInfoFileContent,
    
    // Reset
    resetSave,
  };
};

export default useAdjustmentsSave;
